import type { SvgIconProps } from '@mui/material';
import { Action } from 'kbar';
import React, { useContext } from 'react';
import { makeStyles } from 'tss-react/mui';

import { NavItemList } from 'src/components/sidebar/sidebar-items/NavItemList';
import { SidebarFolderItem } from 'src/components/sidebar/sidebar-items/SidebarFolderItem';
import { SidebarListItem } from 'src/components/sidebar/sidebar-items/SidebarListItem';
import { SidebarSectionTitle } from 'src/components/sidebar/sidebar-items/SidebarSectionTitle';
import * as Constants from 'src/constants';
import {
  GENERAL_SETTINGS_PAGE,
  HELP_CENTER_PAGE,
  PERM_MOD_SETTINGS_MAIN_PG,
  PROFILE_PAGE,
} from 'src/constants';
import { RouteContext, useFeatureFlags } from 'src/context';
import {
  ClientGettingStartedChecklist,
  GettingStartedChecklist,
} from 'src/features/appBuilder/getting-started-checklist';
import { useStudioChecklist } from 'src/features/appBuilder/getting-started-checklist/useStudioChecklist';
import { SidebarNotifications } from 'src/features/referrals/sidebar-promo/SidebarNotifications';
import { SettingsSidebarNav } from 'src/features/settings/sidebar';
import history from 'src/history';
import { useAppBuilderAccess } from 'src/hooks/useAppBuilderAccess';
import { useKeyboardShortcuts } from 'src/hooks/useKeyboardShortcuts';
import useNavigate from 'src/hooks/useNavigate';
import { useSidebarNavigationShortcut } from 'src/hooks/useSidebarNavigationShortcut';
import { useAppDispatch, useAppSelector } from 'src/hooks/useStore';
import { useStudioClientPreview } from 'src/hooks/useStudioClientPreview';
import { useUserNotification } from 'src/hooks/useUserNotification';
import RowDivider from 'src/legacy/components/RowDivider';
import { useSettingsPageRedirection } from 'src/legacy/components/Settings/useSettingsPageRedirection';
import { useRouteAccess } from 'src/routes/routeAccess';
import { carryAdditionalWorkspaceQuery } from 'src/routes/settings/plans/additionalWorkspacePlan';
import { useGetPortalAssistantSettingsQuery } from 'src/services/api';
import { useCustomFields } from 'src/services/api/customFieldsApi';
import {
  CustomFieldEntityType,
  selectActiveCompaniesMap,
} from 'src/store/clients/types';
import { GetCurrentUserFromState } from 'src/store/storeUtils';
import {
  toggleHelpCenterModalAction,
  togglePrimarySidebarMobile,
} from 'src/store/ui/actions';
import { BlackHeadings, NonHoverBorder } from 'src/theme/colors';
import { UrlUtils } from 'src/utils';
import { applyQueryParams } from 'src/utils/ExtensionUtils';
import { hasPermission } from 'src/utils/UserUtils';

import { AddAppSidebarItem } from './AddAppSidebarItem';
import { AppBuilderPlaceholderSidebarItems } from './AppBuilderPlaceholderSidebarItems';
import { ClientExperienceNavItem } from './ClientExperienceNavItem';
import { SidebarBottomBar } from './SidebarBottomBar';
import { getSidebarCommandBarActions } from './sidebarCommandBarActions';
import { isFolderOpenForPath, isNavItemSelected } from './sidebarSelection';
import { SidebarUpdateCard } from './SidebarUpdateCard';
import { StudioUpgradePlanActionCard } from './StudioUpgradePlanActionCard';
import {
  getFirstAccessibleNavItemRedirectPath,
  shouldSidebarRedirectFromPath,
  sidebarLandsOnHomePath,
  useFirstAccessibleNavItem,
} from './useFirstAccessibleNavItem';
import { useSidebarItems } from './useSidebarItems';
import { useSidebarTheme } from './useSidebarTheme';

const useStyles = makeStyles()((theme) => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    height: '100%',
  },
  nested: {
    padding: theme.spacing(0.5, 0, 0.5, 3),
  },
  sidebarItemDivider: {
    padding: theme.spacing(0, 1),
    '& hr': {
      borderColor: NonHoverBorder,
    },
  },
  menuList: {
    padding: theme.spacing(1),
    paddingBottom: 0,
    minWidth: 170,
    outline: 'none',
  },
  copyText: {
    wordBreak: 'break-all',
    color: BlackHeadings,
  },
}));

export interface NavSection {
  htmlId: string;
  title: string;
  navItems: Array<NavItem>;
  canAccess: boolean;
  icon?: (props: SvgIconProps) => JSX.Element;
  navItemsWithFolders?: Array<NavItem>;
}

/**
 * Checks if the item is a (non-folder) NavItem
 */
export function isNavItem(item: any): item is NavItem {
  return (
    Boolean(item) &&
    'htmlId' in item &&
    'label' in item &&
    'path' in item &&
    !item.isFolder
  );
}

export interface NavItem {
  htmlId: string;
  label: string;
  path: string;
  isExternalLink?: boolean;
  icon?: (props: SvgIconProps) => JSX.Element;
  iconName?: string;
  canAccess?: boolean;
  subItems?: Array<NavItem>;
  shouldCollapse?: boolean;
  selected?: boolean;
  children?: React.ReactNode;
  shortcut?: string;
  tooltipTitle?: React.ReactNode;
  disabled?: boolean;
  priority?: number;
  isNonCoreApp?: boolean;
  queryParams?: Record<string, string>[];
  id?: string;
  isFolder?: boolean;
  folderPath?: string;
  isHidden?: boolean;
  notificationCount?: number;
  /** The app builder this entry opens, for callers that pin a tab. */
  builderId?: string;
}

type NavOptions = {
  path: string;
  isExternalLink?: boolean;
  isNonCoreApp?: boolean;
};
const SidebarNav: React.FC = () => {
  const { classes } = useStyles();
  const { pathname } = useContext(RouteContext);
  // The client "Getting started" card is a Studio FTUX surface shown only while
  // previewing the portal as a client ("Test as a client"), not for real clients.
  const { isPreview: isStudioClientPreview, progress: studioPreviewProgress } =
    useStudioClientPreview();
  const { navigate } = useNavigate();
  const isClient = useAppSelector((state) => state.user.isClient);

  const { completedOnLoad: isStudioOnboardingComplete } = useStudioChecklist();
  const loggedInUser = useAppSelector((state) =>
    isClient ? GetCurrentUserFromState(state) : undefined,
  );
  const {
    EnableNewSidebar,
    EnableStudioFTUX,
    EnableGrowthLoops,
    EnableNewSettings,
  } = useFeatureFlags();
  // The checklist onboards into the app builder, so it follows the same plan
  // entitlement: a legacy-plan or kill-switched workspace has no builder to
  // onboard into. Plan access alone, not the full builder gate — an eligible
  // workspace still waiting on the rollout flag keeps its checklist.
  const { hasAppBuilderPlanAccess } = useAppBuilderAccess();
  // The client preview runs on the client's own permissions, which never carry
  // module_appBuilder, so the gate above would always fail there. Read the
  // workspace kill switch instead — it is role-independent.
  const { data: assistantSettings } = useGetPortalAssistantSettingsQuery();
  const isAppBuilderDisabled = Boolean(assistantSettings?.disableAppBuilder);
  const {
    sidebarTheme: {
      sidebarItemHoverBackground,
      sidebarBackgroundColor,
      sidebarTextColor,
    },
  } = useSidebarTheme();
  const { routeAccess } = useRouteAccess();
  const clientCustomFieldsMap = useCustomFields(CustomFieldEntityType.CLIENT);
  const companyCustomFieldsMap = useCustomFields(CustomFieldEntityType.COMPANY);

  useUserNotification(isClient);

  const loggedInUserCompany = useAppSelector((state) => {
    if (!isClient || !loggedInUser) return undefined;
    const activeCompaniesMap = selectActiveCompaniesMap(state);
    const { activeCompanyId } = state.activeCompany;
    return activeCompanyId
      ? activeCompaniesMap.get(activeCompanyId)
      : undefined;
  });
  const dispatch = useAppDispatch();
  const permissions = useAppSelector((state) => state.user.data?.permissions);
  const hasMainSettingsPermission = hasPermission(
    PERM_MOD_SETTINGS_MAIN_PG,
    permissions,
  );
  const settingsLandingPage =
    !isClient && hasMainSettingsPermission
      ? GENERAL_SETTINGS_PAGE
      : PROFILE_PAGE;
  const { goToSettings } = useSettingsPageRedirection(
    true,
    settingsLandingPage.path,
  );
  const isPrimarySidebarOpenOnMobile = useAppSelector(
    (state) => state.ui.primarySideBarMobile.isOpen,
  );

  const getNavHandler =
    (navOptions: NavOptions & Pick<NavItem, 'queryParams'>): (() => void) =>
    () => {
      const { path, isExternalLink, isNonCoreApp, queryParams } = navOptions;

      // when sidebar item has external link, open it in new tab
      let fullPath = UrlUtils.GetFullUrl(path);

      // Apply query parameters if they exist and we have client/company context
      if (isExternalLink && queryParams?.length) {
        fullPath = applyQueryParams(
          fullPath,
          queryParams,
          loggedInUser,
          loggedInUserCompany,
          clientCustomFieldsMap,
          companyCustomFieldsMap,
        );
      }

      if (isExternalLink) {
        window.open(fullPath, '_blank');
        return;
      }

      // otherwise, navigate to path
      navigate(path, { forceReload: isNonCoreApp || false });

      // after navigating to path, close primary sidebar on mobile
      if (isPrimarySidebarOpenOnMobile) {
        dispatch(togglePrimarySidebarMobile({ isOpen: false }));
      }
    };

  const { sections: mainSidebarSections, isLoadingInstalls } =
    useSidebarItems('main');
  const { sections: settingsSidebarSections } = useSidebarItems('settings');

  const isSettingsPage = pathname.includes('settings');
  const showRegistrySettingsSidebar = isSettingsPage && EnableNewSettings;

  const sidebarSections = React.useMemo(
    () =>
      showRegistrySettingsSidebar
        ? []
        : isSettingsPage
          ? settingsSidebarSections
          : mainSidebarSections,
    [
      pathname,
      settingsSidebarSections,
      mainSidebarSections,
      showRegistrySettingsSidebar,
    ],
  );

  const firstAccessibleNavItem = useFirstAccessibleNavItem(
    mainSidebarSections,
    isClient,
  );
  const sidebarLandsOnHome = sidebarLandsOnHomePath(firstAccessibleNavItem);

  /**
   * This holds the main sidebar command bar actions (shortcuts)
   */
  const mainSidebarCommandBarActions: Action[] = React.useMemo(
    () =>
      getSidebarCommandBarActions({
        sections: mainSidebarSections,
        isClient,
        routeAccess,
        navigate,
        goToSettings,
      }),
    [mainSidebarSections, isClient, routeAccess, navigate, goToSettings],
  );

  // register shortcuts actions for main sidebar.
  useKeyboardShortcuts(mainSidebarCommandBarActions);

  // Enable Option+Up/Down keyboard navigation for sidebar (internal users only)
  useSidebarNavigationShortcut();

  /**
   * this use effect will redirect the to the first available sidebar section
   * for the user. Internal user will go to get started if available
   * otherwise user is redirect to first sidebar nav item
   */
  React.useEffect(() => {
    if (
      !shouldSidebarRedirectFromPath({
        pathname,
        sidebarLandsOnHome,
        isClient,
      })
    ) {
      return;
    }

    // Client doesn't have access to CRM and Home (for now).
    // First accessible section for client would have to come from sidebarSections[1]
    // Or else redirection to first nav item won't be possible

    if (firstAccessibleNavItem) {
      // Determine the appropriate redirect path based on the first accessible navigation item.
      // If the first accessible item is an external link, instead of redirecting directly to the external link,
      // we redirect to the apps page using the app ID. This page provides information about the app,
      // and the user can then choose to visit the external link from there.
      const redirectPath = getFirstAccessibleNavItemRedirectPath(
        firstAccessibleNavItem,
      );

      // Avoid redirecting back to the current path
      if (redirectPath !== pathname) {
        history.push(carryAdditionalWorkspaceQuery(redirectPath));
      }
    } else if (pathname !== '/no-apps' && isClient && !isLoadingInstalls) {
      // if the current user is a client and there are no accessible nav items
      // redirect to the no apps page if it is not the current page
      // only redirect after installs data has finished loading
      history.push('/no-apps');
    } else if (!isClient && !isLoadingInstalls) {
      console.error('No accessible sidebar section found for redirection');
    }
  }, [
    firstAccessibleNavItem,
    pathname,
    isClient,
    isLoadingInstalls,
    sidebarLandsOnHome,
  ]);

  const getIsNavItemSelected = (navItem: NavItem) =>
    isNavItemSelected(pathname, navItem);

  const getCollapseItemState = (item: NavItem) =>
    isFolderOpenForPath(pathname, item);

  const accessibleSections = sidebarSections.filter((s) => s.canAccess);

  const isHelpCenterModalOpen = useAppSelector(
    (state) => state.ui.helpCenterModal.isOpen,
  );

  const openHelpCenterModal = () => {
    dispatch(toggleHelpCenterModalAction(true));
  };
  const showDivider = (index: number) =>
    index !== sidebarSections.length - 1 && // Divider not to be displayed for last section
    !isSettingsPage && // Settings page do not have dividers
    !isClient && // Client do not have multiple section, hence no divider
    !EnableNewSidebar && // New sidebar uses section titles instead of dividers
    sidebarSections[index + 1]?.canAccess; // There's next item, so we can display divider

  return (
    <div className={classes.root}>
      <div
        className={
          EnableNewSidebar ? 'flex-1 overflow-y-auto min-h-0' : undefined
        }
      >
        {showRegistrySettingsSidebar && <SettingsSidebarNav />}
        {sidebarSections.map((section, index) => {
          let { navItems } = section;
          let selectedHiddenNavItem: NavItem | undefined;
          if (section.navItemsWithFolders) {
            navItems = section.navItemsWithFolders;
            selectedHiddenNavItem = section.navItems.find(
              (item) => item.isHidden && getIsNavItemSelected(item),
            );
          }
          return (
            <React.Fragment key={section.htmlId}>
              {section.canAccess && (
                <div
                  className={
                    EnableNewSidebar && section !== accessibleSections.at(0)
                      ? 'mt-3'
                      : undefined
                  }
                >
                  {/* Displaying section title and action */}
                  {accessibleSections.length > 1 && section.title && (
                    <SidebarSectionTitle
                      htmlId={section.htmlId}
                      sectionTitle={section.title}
                    >
                      {section.icon && <section.icon />}
                    </SidebarSectionTitle>
                  )}

                  {/* Displaying section nav items */}
                  {navItems.map(
                    (navItem) =>
                      navItem.canAccess &&
                      !navItem.isHidden &&
                      (navItem.subItems ? (
                        <SidebarFolderItem
                          key={navItem.htmlId}
                          htmlId={navItem.htmlId}
                          label={navItem.label}
                          isOpen={
                            navItem.shouldCollapse ||
                            getCollapseItemState(navItem)
                          }
                          notificationCount={navItem.notificationCount}
                          iconName={navItem.iconName}
                        >
                          <div className={classes.nested}>
                            <NavItemList
                              items={navItem.subItems
                                .filter((sub) => sub.canAccess && !sub.isHidden)
                                .map((sub) => ({
                                  id: sub.htmlId,
                                  label: sub.label,
                                  selected: getIsNavItemSelected(sub),
                                  disabled: sub.disabled,
                                  tooltipTitle: sub.tooltipTitle,
                                  itemChildren: sub.children,
                                  onClick: getNavHandler({
                                    path: sub.path,
                                    isExternalLink: sub.isExternalLink,
                                    isNonCoreApp: sub.isNonCoreApp,
                                    queryParams: sub.queryParams,
                                  }),
                                }))}
                            />
                          </div>
                        </SidebarFolderItem>
                      ) : (
                        <SidebarListItem
                          key={navItem.htmlId}
                          htmlId={navItem.htmlId}
                          label={navItem.label}
                          tooltipTitle={navItem.tooltipTitle}
                          disabled={navItem.disabled}
                          iconName={navItem.iconName}
                          selected={getIsNavItemSelected(navItem)}
                          handleClick={getNavHandler({
                            path: navItem.path,
                            isExternalLink: navItem.isExternalLink,
                            isNonCoreApp: navItem.isNonCoreApp,
                            queryParams: navItem.queryParams,
                          })}
                        >
                          {navItem.children}
                        </SidebarListItem>
                      )),
                  )}
                  {selectedHiddenNavItem && (
                    <SidebarListItem
                      htmlId={selectedHiddenNavItem.htmlId}
                      label={selectedHiddenNavItem.label}
                      tooltipTitle={selectedHiddenNavItem.tooltipTitle}
                      disabled={selectedHiddenNavItem.disabled}
                      iconName={selectedHiddenNavItem.iconName}
                      selected
                    >
                      {selectedHiddenNavItem.children}
                    </SidebarListItem>
                  )}
                  {(section.htmlId === 'section-modules' ||
                    section.htmlId === 'section-apps') && (
                    <>
                      <AppBuilderPlaceholderSidebarItems />
                      <AddAppSidebarItem />
                    </>
                  )}
                </div>
              )}
              {showDivider(index) && (
                <div className={classes.sidebarItemDivider}>
                  <RowDivider mt={1} mb={1} />
                </div>
              )}
            </React.Fragment>
          );
        })}
      </div>

      {!isSettingsPage && (
        <div>
          {EnableNewSidebar ? (
            <div className="px-2 pt-2">
              {!isClient && !EnableGrowthLoops && <SidebarUpdateCard />}
              {!isClient && EnableStudioFTUX && hasAppBuilderPlanAccess && (
                <GettingStartedChecklist />
              )}
              {!isClient && EnableStudioFTUX && isStudioOnboardingComplete && (
                <StudioUpgradePlanActionCard />
              )}
              {/* Growth-loops design moves the update card below the
                  checklist, stacked with the referral promo. */}
              {!isClient && EnableGrowthLoops && <SidebarNotifications />}
              {isClient &&
                EnableStudioFTUX &&
                isStudioClientPreview &&
                !isAppBuilderDisabled &&
                studioPreviewProgress > 0 && (
                  <ClientGettingStartedChecklist
                    progress={studioPreviewProgress}
                    backgroundColor={sidebarItemHoverBackground}
                    sidebarColor={sidebarBackgroundColor}
                    textColor={sidebarTextColor}
                  />
                )}
              <SidebarBottomBar
                onSettingsClick={() => goToSettings()}
                onHelpCenterClick={openHelpCenterModal}
                isClient={isClient}
                isHelpCenterOpen={isHelpCenterModalOpen}
                isSettingsSelected={pathname === Constants.PROFILE_PAGE.path}
              />
            </div>
          ) : (
            <>
              {/* Under growth-loops the program lives in settings only. */}
              {routeAccess.allowReferralPage && !EnableGrowthLoops && (
                <SidebarListItem
                  htmlId="sidebar-referrals"
                  label="Referrals"
                  selected={pathname === Constants.REFERRAL_PAGE_V2.path}
                  handleClick={getNavHandler({
                    path: Constants.REFERRAL_PAGE_V2.path,
                  })}
                  iconName="gift"
                />
              )}
              {!isClient && (
                <SidebarListItem
                  htmlId="sidebar-help-center"
                  label={HELP_CENTER_PAGE.label}
                  selected={isHelpCenterModalOpen}
                  handleClick={openHelpCenterModal}
                  iconName="circle-question"
                />
              )}

              <SidebarListItem
                htmlId="sidebar-settings"
                label="Settings"
                selected={pathname === Constants.PROFILE_PAGE.path}
                handleClick={() => {
                  goToSettings();
                }}
                iconName="SettingsSolid"
              />
              {!isClient && <ClientExperienceNavItem />}
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default SidebarNav;
